import {
  sessionUserSchema,
  drawingSchema,
  instrumentSchema,
  instrumentSearchResultSchema,
  positionViewSchema,
  stopLossEventSchema,
  systemStatusSchema,
  ROUTES,
  stopLossSyncResultSchema,
  type BarSeries,
  type CreateDrawingInput,
  type CreatePositionInput,
  type Drawing,
  type Instrument,
  type InstrumentSearchResult,
  type EntityId,
  type PositionView,
  type SessionUser,
  type StopLossEvent,
  type StopLossSyncResultData,
  type SystemStatus,
  type UpdateDrawingInput,
  type UpdatePositionInput,
} from '@stockstop/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { api } from './client.js';

/**
 * Server state.
 *
 * All cache keys are defined in one object so an invalidation cannot miss a query
 * through a mistyped key. Mutations invalidate the portfolio because a changed
 * stop-loss, a new position or a reorder all alter the derived view the server
 * computes.
 */
export const queryKeys = {
  positions: ['positions'] as const,
  instruments: ['instruments'] as const,
  instrumentSearch: (query: string) => ['instruments', 'search', query] as const,
  bars: (instrumentId: number) => ['bars', instrumentId] as const,
  drawings: (instrumentId: number) => ['drawings', instrumentId] as const,
  syncState: ['sync', 'state'] as const,
  systemStatus: ['system', 'status'] as const,
  history: (positionId: number) => ['positions', positionId, 'history'] as const,
  session: ['session'] as const,
};

/**
 * Wer ist angemeldet? `null`, wenn niemand.
 *
 * Die Antwort entscheidet, ob die Anwendung oder die Anmeldeseite erscheint, und
 * ist damit die erste Abfrage überhaupt. Sie ist bewusst lange haltbar: Eine
 * Sitzung läuft nicht binnen Sekunden ab, und ein Ablauf fällt ohnehin sofort auf
 * — jede andere Abfrage antwortet dann mit 401, was den Eintrag verwirft.
 */
export const useSession = (): UseQueryResult<SessionUser | null> =>
  useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) =>
      api.get(ROUTES.auth.me, { signal, schema: sessionUserSchema.nullable() }),
    staleTime: 5 * 60 * 1000,
  });

/**
 * Abmelden.
 *
 * Danach wird der gesamte Zwischenspeicher geleert, nicht nur die Sitzung. Sonst
 * sähe der nächste Anmeldende an diesem Browser für einen Moment die Positionen
 * des vorigen — bei getrennten Mandanten wäre das genau der Bruch, den die
 * Trennung verhindern soll.
 */
export const useLogout = (): UseMutationResult<{ abgemeldet: true }, Error, void> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ abgemeldet: true }>(ROUTES.auth.logout),
    onSuccess: () => {
      client.clear();
    },
  });
};

/**
 * Systemzustand inklusive aktivem Marktdaten-Provider.
 *
 * Wird regelmäßig nachgeladen, damit ein Providerwechsel in der `.env` nach einem
 * Neustart der API auch ohne Neuladen der Seite sichtbar wird.
 */
export const useSystemStatus = (): UseQueryResult<SystemStatus> =>
  useQuery({
    queryKey: queryKeys.systemStatus,
    queryFn: ({ signal }) => api.get(ROUTES.health, { signal, schema: systemStatusSchema }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

/**
 * Das Portfolio des angemeldeten Mandanten.
 *
 * `enabled` hängt an der Anmeldung: Ohne sie antwortet die Route mit 401, und der
 * Nutzer sähe eine Fehlermeldung, wo eigentlich die Anmeldeseite hingehört. Die
 * Haken laufen in React vor jedem vorzeitigen `return`, die Abfrage muss sich also
 * selbst zurückhalten.
 */
export const usePositions = (options: { enabled?: boolean } = {}): UseQueryResult<PositionView[]> =>
  useQuery({
    queryKey: queryKeys.positions,
    queryFn: ({ signal }) =>
      api.get(ROUTES.positions.list, { signal, schema: z.array(positionViewSchema) }),
    enabled: options.enabled ?? true,
  });

/**
 * Speichert die Reihenfolge des Portfolios.
 *
 * Die neue Reihenfolge wird sofort angezeigt und erst danach bestätigt. Beim
 * Ziehen ist das keine Bequemlichkeit, sondern Voraussetzung: Springt die Karte
 * nach dem Loslassen an ihren alten Platz zurück, bis der Server geantwortet hat,
 * wirkt die Bedienung kaputt — und man zieht ein zweites Mal.
 *
 * Scheitert der Aufruf, wird die vorherige Reihenfolge zurückgelegt.
 */
export const useReorderPositions = (): UseMutationResult<PositionView[], Error, EntityId[]> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: EntityId[]) =>
      api.post<PositionView[]>(ROUTES.positions.reorder, { orderedIds }),

    onMutate: async (orderedIds) => {
      /* Laufende Abfragen anhalten, sonst überschreibt eine verspätete Antwort
       * die vorgezogene Reihenfolge wieder. */
      await client.cancelQueries({ queryKey: queryKeys.positions });
      const previous = client.getQueryData<PositionView[]>(queryKeys.positions);

      if (previous) {
        const byId = new Map(previous.map((position) => [position.id, position]));
        const next = orderedIds
          .map((id) => byId.get(id))
          .filter((position): position is PositionView => position !== undefined);
        client.setQueryData(queryKeys.positions, next);
      }

      return { previous };
    },

    onError: (_error, _ids, context) => {
      if (context?.previous) client.setQueryData(queryKeys.positions, context.previous);
    },

    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.positions });
    },
  });
};

export const useInstruments = (): UseQueryResult<Instrument[]> =>
  useQuery({
    queryKey: queryKeys.instruments,
    queryFn: ({ signal }) =>
      api.get(ROUTES.instruments.list, { signal, schema: z.array(instrumentSchema) }),
  });

export const useInstrumentSearch = (query: string): UseQueryResult<InstrumentSearchResult[]> =>
  useQuery({
    queryKey: queryKeys.instrumentSearch(query),
    queryFn: ({ signal }) =>
      api.get(`${ROUTES.instruments.search}?q=${encodeURIComponent(query)}`, {
        signal,
        schema: z.array(instrumentSearchResultSchema),
      }),
    enabled: query.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Die Kerzenreihe wird bewusst **nicht** gegen ihr Schema geprüft.
 *
 * Bis zu 20.000 Kerzen bei jedem Wertwechsel durch Zod zu schicken kostet
 * spürbar Zeit, und der Nutzen wäre gering: Die Reihe ist eine flache Liste aus
 * Zahlen, die sich seit der ersten Fassung nicht geändert hat. Geprüft wird dort,
 * wo Schemas wachsen — bei Positionen, Zeichnungen und dem Verlauf.
 */
export const useBars = (instrumentId: number | null): UseQueryResult<BarSeries> =>
  useQuery({
    queryKey: queryKeys.bars(instrumentId ?? 0),
    queryFn: ({ signal }) =>
      api.get<BarSeries>(`${ROUTES.bars.byInstrument(instrumentId!)}?limit=20000`, { signal }),
    enabled: instrumentId !== null,
    /*
     * Tageskerzen ändern sich einmal täglich. Ein langes Intervall hielte aber
     * nach einem Import im Terminal weiterhin die alte, womöglich leere Antwort
     * fest — genau das ließ den Chart nach einem Providerwechsel leer wirken.
     */
    staleTime: 60_000,
  });

export const useDrawings = (instrumentId: number | null): UseQueryResult<Drawing[]> =>
  useQuery({
    queryKey: queryKeys.drawings(instrumentId ?? 0),
    queryFn: ({ signal }) =>
      api.get(ROUTES.drawings.byInstrument(instrumentId!), {
        signal,
        schema: z.array(drawingSchema),
      }),
    enabled: instrumentId !== null,
  });

export const useCreatePosition = (): UseMutationResult<
  PositionView,
  Error,
  CreatePositionInput
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePositionInput) =>
      api.post<PositionView>(ROUTES.positions.create, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.positions });
      void client.invalidateQueries({ queryKey: queryKeys.instruments });
    },
  });
};

export const useUpdatePosition = (): UseMutationResult<
  PositionView,
  Error,
  { id: number; input: UpdatePositionInput }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => api.patch<PositionView>(ROUTES.positions.byId(id), input),
    onSuccess: (updated) => {
      void client.invalidateQueries({ queryKey: queryKeys.positions });
      /*
       * Die Stop-Loss-Linie im Chart zeigt den Stop der Position. Ändert sich
       * dieser, muss sie nachgeladen werden — sonst widerspräche der Chart der
       * Karte, aus der die Änderung kam.
       */
      void client.invalidateQueries({ queryKey: queryKeys.drawings(updated.instrumentId) });
      void client.invalidateQueries({ queryKey: queryKeys.history(updated.id) });
    },
  });
};

/**
 * Bucht den aktuellen Stop-Loss als beim Broker hinterlegt ein.
 *
 * Der Kurs wird nicht mitgeschickt: Maßgeblich ist, was der Server im Moment der
 * Bestätigung berechnet. Würde die Oberfläche ihn mitsenden, könnte ein veralteter
 * Wert aus dem Zwischenspeicher eingebucht werden.
 */
/** Verlauf der Stop-Loss-Entscheidungen einer Position. */
export const useStopLossHistory = (positionId: number | null): UseQueryResult<StopLossEvent[]> =>
  useQuery({
    queryKey: queryKeys.history(positionId ?? 0),
    queryFn: ({ signal }) =>
      api.get(ROUTES.positions.history(positionId!), {
        signal,
        schema: z.array(stopLossEventSchema),
      }),
    enabled: positionId !== null,
  });

export const useBookStopLoss = (): UseMutationResult<
  PositionView,
  Error,
  { id: number; validUntil: string }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, validUntil }) =>
      api.post<PositionView>(ROUTES.positions.booking(id), { validUntil }),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.positions });
      void client.invalidateQueries({ queryKey: queryKeys.history(variables.id) });
    },
  });
};

export const useClearBooking = (): UseMutationResult<PositionView, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<PositionView>(ROUTES.positions.booking(id)),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.positions }),
  });
};

export const useDeletePosition = (): UseMutationResult<{ deleted: boolean }, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: boolean }>(ROUTES.positions.byId(id)),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.positions }),
  });
};

export const useCreateDrawing = (
  instrumentId: number,
): UseMutationResult<Drawing, Error, CreateDrawingInput> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDrawingInput) =>
      api.post<Drawing>(ROUTES.drawings.byInstrument(instrumentId), input),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.drawings(instrumentId) }),
  });
};

export const useUpdateDrawing = (
  instrumentId: number,
): UseMutationResult<Drawing, Error, { id: number; input: UpdateDrawingInput }> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.patch<Drawing>(ROUTES.drawings.byId(instrumentId, id), input),
    /*
     * Das Ziehen hat die Zeichnung bereits auf dem Schirm bewegt; ein erneutes
     * Laden würde dieselbe Geometrie zurückgeben. Der Zwischenspeicher wird
     * deshalb direkt angepasst.
     */
    onSuccess: (updated) => {
      client.setQueryData<Drawing[]>(queryKeys.drawings(instrumentId), (current) =>
        current?.map((drawing) => (drawing.id === updated.id ? updated : drawing)),
      );

      /* Ein Zug an der Stop-Loss-Linie hat den Stop der Position verändert. */
      if (updated.payload.type === 'stop_loss') {
        void client.invalidateQueries({ queryKey: queryKeys.positions });
        /* Das Ziehen der Linie hat eine Entscheidung protokolliert. */
        void client.invalidateQueries({ queryKey: ['positions'] });
      }
    },
  });
};

/**
 * Verbindet einen Indikator mit der Stop-Loss-Linie und löst die Verbindung wieder.
 *
 * Beides lädt die Liste neu statt den Zwischenspeicher zu flicken: Es ändert sich
 * mehr als die eine Zeichnung — sie bekommt eine andere Farbe, wird unlöschbar,
 * und die Bezugsgröße der täglichen Auswertung entsteht oder verfällt. Ein
 * punktuelles Setzen bildete davon nur die Hälfte ab.
 */
export const useLinkDrawing = (instrumentId: number): UseMutationResult<Drawing, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Drawing>(ROUTES.drawings.link(instrumentId, id)),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.drawings(instrumentId) }),
  });
};

export const useUnlinkDrawing = (
  instrumentId: number,
): UseMutationResult<Drawing, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<Drawing>(ROUTES.drawings.link(instrumentId, id)),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.drawings(instrumentId) }),
  });
};

/**
 * Zieht die Stop-Loss-Linie auf den Bezugsabstand des verbundenen Paares nach.
 *
 * Lädt danach Zeichnungen **und** Positionen neu: Der Zug an der Linie setzt den
 * Stop der Position, und die Karte links zeigt ihn. Nur die Zeichnungen zu
 * erneuern hieße, dass Chart und Karte verschiedene Kurse zeigen, bis irgendetwas
 * anderes die Liste anfasst.
 */
export const useSyncStopLoss = (
  instrumentId: number,
): UseMutationResult<StopLossSyncResultData, Error, void> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<StopLossSyncResultData>(ROUTES.drawings.syncStopLoss(instrumentId), undefined, {
        schema: stopLossSyncResultSchema,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.drawings(instrumentId) });
      void client.invalidateQueries({ queryKey: queryKeys.positions });
    },
  });
};

export const useDeleteDrawing = (
  instrumentId: number,
): UseMutationResult<{ deleted: boolean }, Error, number> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(ROUTES.drawings.byId(instrumentId, id)),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.drawings(instrumentId) }),
  });
};

/*
 * Einen Abgleich von der Oberfläche aus auszulösen gibt es bewusst nicht mehr.
 *
 * Wer während der Handelszeit abgleicht, speichert eine unfertige Tageskerze als
 * fertigen Schlusskurs; weil der nächste Lauf erst am Folgetag ansetzt, bliebe
 * sie dauerhaft falsch. Der Abgleich gehört deshalb allein dem Nachtlauf nach
 * Börsenschluss. Ein neu angelegter Wert holt seine Historie einmalig serverseitig
 * beim Anlegen — siehe `position.routes.ts`.
 */
